import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useChatUnread } from '../hooks/useChatUnread';
import { cartAPI } from '../services/api';
import { 
  MagnifyingGlassIcon, 
  ShoppingCartIcon, 
  HeartIcon, 
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  GiftIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { STAFF_ROLE_GROUPS, hasRole } from '../utils/roles';

const LogoImage = memo(({ src, alt, className, width, height }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!src) return undefined;
    let canceled = false;
    const img = new window.Image();
    img.decoding = 'async';
    img.src = src;
    const done = () => {
      if (!canceled) setReady(true);
    };
    if (img.decode) {
      img
        .decode()
        .catch(() => undefined)
        .finally(done);
    } else {
      img.onload = done;
      img.onerror = done;
    }
    return () => {
      canceled = true;
    };
  }, [src]);

  const placeholderClassName = useMemo(() => {
    const base = String(className || '');
    return `${base} bg-gray-100`;
  }, [className]);

  if (!src) return null;

  return (
    <>
      {!ready ? (
        <div
          aria-hidden="true"
          className={placeholderClassName}
          style={{
            width: width ? `${width}px` : undefined,
            height: height ? `${height}px` : undefined,
          }}
        />
      ) : null}
      {ready ? (
        <img
          src={src}
          alt={alt}
          decoding="async"
          loading="eager"
          fetchpriority="high"
          style={{ imageRendering: 'auto', display: 'block' }}
          width={width}
          height={height}
          className={className}
        />
      ) : null}
    </>
  );
});

const Header = ({
  onSearch,
  cartItemsCount: cartItemsCountProp = 0,
  hideMainHeaderOnMobile = false,
  hideMainHeader = false,
  disableHeaderCollapse = false,
  disableLoopBar = false,
  autoShowLoopBarOnMobile = false,
  loopBarRevealScrollY = null,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  // نام کامل (نام + نام خانوادگی) و نام کوتاه برای نمایش در هدر دسکتاپ/تبلت
  const userFullName = React.useMemo(() => {
    const combined = [user?.first_name, user?.last_name].map((s) => String(s || '').trim()).filter(Boolean).join(' ');
    return combined || String(user?.name || '').trim();
  }, [user]);
  const userShortName = String(user?.first_name || '').trim() || (userFullName ? userFullName.split(' ')[0] : '') || 'کاربر';
  const { systemSettings, resolveMediaUrl } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  // On chats list page, keep red top bar but remove lower header on mobile
  const isChatsPage = location.pathname === '/chats';
  const isHomePage = location.pathname === '/' || location.pathname === '';
  const chatUnread = useChatUnread();
  const [internalCartCount, setInternalCartCount] = useState(cartItemsCountProp);
  useEffect(() => { setInternalCartCount(cartItemsCountProp); }, [cartItemsCountProp]);
  const cartItemsCount = internalCartCount;

  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const res = await cartAPI.getCart();
        const c = res?.data?.items?.reduce((s, it) => s + (Number(it?.quantity) || 0), 0) || 0;
        if (mounted) setInternalCartCount(c);
      } catch {
        try {
          const mod = await import('../utils/guestCart');
          if (mounted) setInternalCartCount(mod.getGuestCartCount());
        } catch {}
      }
    };
    fetchCount();
    const onCartChanged = () => fetchCount();
    window.addEventListener('kadolar:cart-changed', onCartChanged);
    const onStorage = (e) => {
      if (e && e.key && (e.key === 'kadolar_guest_cart_v1' || e.key === 'kadolar_cart_updated_at' || e.key === 'kadolar_cart_updated_at')) fetchCount();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      mounted = false;
      window.removeEventListener('kadolar:cart-changed', onCartChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated, cartItemsCountProp]);

  const [isMdUp, setIsMdUp] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });
  // P3 (بند ۴.۶): extractRoles/hasRole محلی حذف شد — ملاک، گروه‌های نقش از
  // utils/roles است (هم‌راستا با StaffRoute و بک‌اند).
  const isSuperuser = user?.is_superuser === true;
  const canAccessStaff = isSuperuser || STAFF_ROLE_GROUPS.some((r) => hasRole(user, r));
  // فقط تعیین فضایی/سایز آیکون‌ها (staff-ها معمولاً منوی چگالی‌دارتری می‌بینند)
  const showCompactHeader = isAuthenticated && canAccessStaff;

  const [collapseMain, setCollapseMain] = useState(false);
  const [loopBarVisible, setLoopBarVisible] = useState(false);
  const headerCollapseEnabled = !disableHeaderCollapse && systemSettings?.header_collapse_enabled === true;
  const TH = Number.isFinite(Number(systemSettings?.header_scroll_threshold))
    ? Number(systemSettings.header_scroll_threshold)
    : 140;
  const HS = Number.isFinite(Number(systemSettings?.header_scroll_hysteresis))
    ? Number(systemSettings.header_scroll_hysteresis)
    : 120;
  const loopBarEnabled = !disableLoopBar && systemSettings?.loop_bar_enabled !== false;
  const loopBarShowOnHomeMobile = systemSettings?.loop_bar_show_on_home_mobile === true;
  // per-page override — compact
  const getPageKey = (path) => {
    const p = String(path || '').split('?')[0];
    if (p === '/' || p === '') return 'home';
    if (p.startsWith('/categories')) return 'categories';
    if (p.startsWith('/category/')) return 'category';
    if (p.startsWith('/product/')) return 'product';
    if (p.startsWith('/introductions/')) return 'introductions';
    if (p.startsWith('/cart')) return 'cart';
    if (p.startsWith('/checkout')) return 'checkout';
    if (p.startsWith('/profile')) return 'profile';
    if (p.startsWith('/orders')) return 'orders';
    if (p.startsWith('/wishlist')) return 'wishlist';
    if (p.startsWith('/search')) return 'search';
    if (p.startsWith('/contact')) return 'contact';
    if (p.startsWith('/about')) return 'about';
    if (p.startsWith('/terms')) return 'terms';
    if (p.startsWith('/chats') || p.startsWith('/chat')) return 'chats';
    if (p.startsWith('/staff')) return 'staff';
    return 'home';
  };
  const pageKey = getPageKey(location.pathname);
  const perPage = systemSettings?.loop_bar_pages;
  const isPageEnabled = perPage && typeof perPage === 'object' && Object.keys(perPage).length > 0 ? perPage[pageKey] !== false : true;
  const loopBarEnabledEffective = loopBarEnabled && isPageEnabled;
  const loopBarDelayMs = Number.isFinite(Number(systemSettings?.loop_bar_delay_ms))
    ? Number(systemSettings.loop_bar_delay_ms)
    : 1000;
  const loopBarSpeedSeconds = Number.isFinite(Number(systemSettings?.loop_bar_speed_seconds))
    ? Number(systemSettings.loop_bar_speed_seconds)
    : 126;
  const loopBarHeightPx = Number.isFinite(Number(systemSettings?.loop_bar_height_px))
    ? Number(systemSettings.loop_bar_height_px)
    : 50;
  const loopBarItemGapPx = Number.isFinite(Number(systemSettings?.loop_bar_item_gap_px))
    ? Math.max(0, Number(systemSettings.loop_bar_item_gap_px))
    : 32;
  const loopBarEndGapPx = Number.isFinite(Number(systemSettings?.loop_bar_end_gap_px))
    ? Math.max(0, Number(systemSettings.loop_bar_end_gap_px))
    : 200;
  const loopBarBgCss = systemSettings?.loop_bar_bg_css || 'linear-gradient(to right, #7a0000, #c10000, #7a0000)';
  const loopBarTextColor = systemSettings?.loop_bar_text_color || '#ffffff';
  const siteFontFamily = '"Iranian Sans", "IRANSans", Tahoma, Arial, sans-serif';
  const mainHeaderFontFamily = 'Tahoma, Arial, sans-serif';
  const loopBarDirection = String(systemSettings?.loop_bar_direction || '').toLowerCase() === 'ltr' ? 'ltr' : 'rtl';
  const loopBarMaxWidthPx = systemSettings?.loop_bar_max_width_px === null || systemSettings?.loop_bar_max_width_px === undefined
    ? null
    : (Number.isFinite(Number(systemSettings.loop_bar_max_width_px)) ? Number(systemSettings.loop_bar_max_width_px) : null);
  const loopBarTexts = useMemo(() => {
    return Array.isArray(systemSettings?.loop_bar_texts) ? systemSettings.loop_bar_texts.filter(Boolean) : [];
  }, [systemSettings?.loop_bar_texts]);
  const loopBarMarquee = useMemo(() => {
    const Track = ({ ariaHidden, delaySeconds }) => (
      <div
        className="kadolar-marquee-track px-4"
        aria-hidden={ariaHidden}
        style={{
          animationDuration: `${loopBarSpeedSeconds}s`,
          animationDelay: `${delaySeconds}s`,
          animationDirection: loopBarDirection === 'ltr' ? 'reverse' : 'normal',
        }}
      >
        {loopBarTexts.map((t, idx) => (
          <span
            key={`${idx}-${t}`}
            className="font-semibold"
            style={{ marginInlineStart: idx === 0 ? 0 : `${loopBarItemGapPx}px` }}
          >
            {t}
          </span>
        ))}
        <span aria-hidden="true" style={{ display: 'inline-block', width: `${loopBarEndGapPx}px` }} />
      </div>
    );
    return (
      <>
        <Track ariaHidden={false} delaySeconds={0} />
        <Track ariaHidden={true} delaySeconds={-(loopBarSpeedSeconds / 2)} />
      </>
    );
  }, [loopBarDirection, loopBarEndGapPx, loopBarItemGapPx, loopBarSpeedSeconds, loopBarTexts]);
  const headerModeRef = useRef(false);
  const loopBarTimerRef = useRef(null);
  const prevShouldScheduleRef = useRef(false);
  const mainHeaderRef = useRef(null);
  const [mainHeaderInView, setMainHeaderInView] = useState(true);
  const [loopBarGatePassed, setLoopBarGatePassed] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsMdUp(!!e.matches);
    setIsMdUp(!!mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const el = mainHeaderRef.current;
      if (!el) {
        setMainHeaderInView(true);
      } else {
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const inView = r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.left < vw && r.top < vh;
        setMainHeaderInView(inView);
      }
      const y = window.scrollY || 0;
      const gate = Number.isFinite(Number(loopBarRevealScrollY)) ? y >= Number(loopBarRevealScrollY) : true;
      setLoopBarGatePassed(gate);
    };
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [loopBarRevealScrollY]);

  useEffect(() => {
    if (!headerCollapseEnabled) {
      headerModeRef.current = false;
      setCollapseMain(false);
      return;
    }
    const onScroll = () => {
      const y = window.scrollY || 0;
      let nextMode = headerModeRef.current;
      if (y > TH + HS) nextMode = true;
      else if (y < TH - HS) nextMode = false;

      if (nextMode !== headerModeRef.current) {
        headerModeRef.current = nextMode;
        setCollapseMain(nextMode);
      }
    };
    const initialY = window.scrollY || 0;
    const initialMode = initialY > TH;
    headerModeRef.current = initialMode;
    setCollapseMain(initialMode);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [headerCollapseEnabled, TH, HS]);

  useEffect(() => {
    if (loopBarTimerRef.current) {
      clearTimeout(loopBarTimerRef.current);
      loopBarTimerRef.current = null;
    }

    if (!loopBarEnabledEffective || loopBarTexts.length === 0) {
      setLoopBarVisible(false);
      prevShouldScheduleRef.current = false;
      return undefined;
    }

    const shouldSchedule = mainHeaderInView === false && loopBarGatePassed === true;
    if (!shouldSchedule) {
      setLoopBarVisible(false);
      prevShouldScheduleRef.current = false;
      return undefined;
    }

    const effectiveDelayMs = autoShowLoopBarOnMobile && !isMdUp ? 0 : loopBarDelayMs;
    if (!prevShouldScheduleRef.current) {
      if (effectiveDelayMs <= 0) {
        setLoopBarVisible(true);
      } else {
        loopBarTimerRef.current = setTimeout(() => {
          setLoopBarVisible(true);
        }, effectiveDelayMs);
      }
    }
    prevShouldScheduleRef.current = true;

    return () => {
      if (loopBarTimerRef.current) {
        clearTimeout(loopBarTimerRef.current);
        loopBarTimerRef.current = null;
      }
    };
  }, [loopBarEnabledEffective, loopBarDelayMs, mainHeaderInView, loopBarGatePassed, autoShowLoopBarOnMobile, isMdUp, loopBarTexts.length]);

  const showLoopBarHomeMobile =
    loopBarEnabledEffective &&
    loopBarShowOnHomeMobile &&
    !isMdUp &&
    isHomePage &&
    loopBarTexts.length > 0 &&
    !hideMainHeader &&
    !(isChatsPage || hideMainHeaderOnMobile);

  // On /chats the main header is hidden on mobile, so always show the fixed loop bar at top
  // (it acts as the top bar there) instead of waiting for the (hidden) main header to scroll away.
  const showLoopBar = loopBarEnabledEffective && loopBarVisible && ((isChatsPage ? true : mainHeaderInView === false)) && loopBarGatePassed === true && loopBarTexts.length > 0;
  const showLoopBarFixed = showLoopBar && !showLoopBarHomeMobile;

  // In desktop, main header is static (relative). Loop bar should be fixed at top and appear only when scrolled down.
  // When at top (not scrolled), loop bar should be hidden.
  
  const mainHeaderVisible = !hideMainHeader && !collapseMain && (!isChatsPage || isMdUp);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = (searchQuery || '').toString().trim();
    if (!q) return;
    if (onSearch) onSearch(q);
    else navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const reserveTopSpace = !isMdUp && !hideMainHeader && !((isChatsPage || hideMainHeaderOnMobile));
  const reserveTopSpaceHeight = reserveTopSpace ? (104 + (showLoopBarHomeMobile ? loopBarHeightPx : 0)) : 0;

  return (
    <>
      {reserveTopSpace ? <div className="md:h-[160px]" style={{ height: `${reserveTopSpaceHeight}px` }} /> : null}
      <header
        className={`${isMdUp ? 'relative' : 'fixed'} z-[1000] w-full left-0 right-0`}
        style={{ top: (!isMdUp && showLoopBarFixed && mainHeaderVisible) ? `${loopBarHeightPx}px` : 0 }}
      >
      {/* Enhanced Top Bar - Premium Services */}
      <div
        className={`topbar-scrollbar-hide !fixed top-0 inset-x-0 overflow-hidden transition-all duration-500 z-[1100] flex items-center ${showLoopBarFixed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}
        style={{
          height: `${loopBarHeightPx}px`,
          backgroundImage: loopBarBgCss,
          color: loopBarTextColor,
          fontFamily: siteFontFamily,
        }}
      >
        <div
          className="w-full overflow-hidden h-full"
          style={{
            maxWidth: loopBarMaxWidthPx ? `${loopBarMaxWidthPx}px` : undefined,
            margin: loopBarMaxWidthPx ? '0 auto' : undefined,
          }}
        >
          <div className="kadolar-marquee-wrap">
            {loopBarMarquee}
          </div>
        </div>
      </div>

      {/* Main Header (hidden on mobile for /chats) */}
      <div
        className={`bg-white shadow-xl border-b border-gray-100 w-full overflow-visible transition-[max-height,opacity,transform] duration-300 ease-out ${collapseMain ? 'max-h-0 -translate-y-2 opacity-0 pointer-events-none' : 'max-h-[520px] opacity-100'} ${hideMainHeader ? 'hidden' : (isChatsPage || hideMainHeaderOnMobile ? 'hidden md:block' : '')}`}
        ref={mainHeaderRef}
        style={{ fontFamily: mainHeaderFontFamily }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 overflow-y-visible">
        <div className="w-full flex flex-wrap items-center justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-6 py-4 lg:py-6">
          <div className="hidden md:flex items-center gap-4 justify-self-start">
            <Menu as="div" className="relative">
              <Menu.Button className="relative h-12 w-12 p-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-colors duration-300 group border-2 border-gray-200 hover:border-kadolar-300 shadow-md hover:shadow-lg">
                <Bars3Icon className="h-6 w-6 text-gray-700 group-hover:text-kadolar-600 transition-colors duration-300" />
              </Menu.Button>
              <Transition
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-4 w-72 bg-white rounded-3xl shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-[700] border-2 border-gray-100 overflow-hidden">
                  <div className="py-2">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/categories"
                          className={`${active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'} flex items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                        >
                          دسته‌بندی‌ها
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/orders"
                          className={`${active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'} flex items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                        >
                          سفارشات من
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/about"
                          className={`${active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'} flex items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                        >
                          درباره ما
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/contact"
                          className={`${active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'} flex items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                        >
                          ارتباط با ما
                        </Link>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <div className="hidden lg:block w-[454px] max-w-[38vw]">
              <form onSubmit={handleSearch} className="w-full relative group">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-kadolar-200 to-kadolar-300 rounded-3xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="جستجو در میان هزاران هدیه لوکس و منحصر به فرد..."
                    className="w-full pl-14 pr-6 py-4 border-2 border-gray-200 rounded-3xl focus:ring-4 focus:ring-kadolar-200 focus:border-kadolar-400 bg-gradient-to-r from-gray-50 to-white hover:from-white hover:to-gray-50 transition-all duration-500 text-gray-800 placeholder-gray-500 font-medium text-base shadow-lg hover:shadow-xl relative z-10"
                  />
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center z-20">
                    <button
                      type="submit"
                      className="p-2 text-gray-500 hover:text-kadolar-600 transition-colors duration-300"
                    >
                      <MagnifyingGlassIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="flex md:hidden items-end w-full gap-3">
            <div className="flex-1 min-w-0 flex justify-start">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-[7px] bg-gradient-to-br from-gray-50 to-gray-100 hover:from-kadolar-50 hover:to-kadolar-100 rounded-md transition-all duration-500 group border-2 border-gray-200 hover:border-kadolar-300 shadow-lg hover:shadow-xl transform hover:scale-110"
                aria-label="باز کردن منو"
              >
                <div className="relative">
                  {mobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6 text-gray-700 group-hover:text-kadolar-600 transition-colors duration-300 transform scale-90" />
                  ) : (
                    <Bars3Icon className="h-6 w-6 text-gray-700 group-hover:text-kadolar-600 transition-colors duration-300 transform scale-90" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-kadolar-400 to-kadolar-600 rounded opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </div>
              </button>
            </div>

            <div className="relative w-[67px] h-[67px] shrink-0 flex items-center justify-center">
              <Link
                to="/"
                aria-label={systemSettings?.site_name || 'کادولار'}
                className="w-[67px] h-[67px] flex items-center justify-center overflow-hidden"
              >
                {systemSettings?.logo_url ? (
                  <LogoImage
                    src={resolveMediaUrl(systemSettings.logo_url)}
                    alt={systemSettings?.site_name || 'لوگو'}
                    width="67"
                    height="67"
                    className="w-[67px] h-[67px] object-contain"
                  />
                ) : (
                  <GiftIcon className="h-9 w-9 text-kadolar-700 " />
                )}
              </Link>

              <span className="hidden sm:block absolute left-full ml-2 whitespace-nowrap text-[14px] font-black tracking-[0.22em] bg-gradient-to-r from-kadolar-600 via-kadolar-700 to-kadolar-800 bg-clip-text text-transparent select-none">
                خوش آمدید به
              </span>
            </div>

            <div className="flex-1 min-w-0 flex justify-end">
              <form onSubmit={handleSearch} className="w-[30.4vw] min-w-[110px] max-w-[207px]">
                <div className="w-full bg-white rounded-3xl border-2 border-gray-200 shadow-lg overflow-hidden">
                  <div className="flex items-center">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder=" "
                        className="w-full py-[7px] px-3 text-gray-800 placeholder-transparent font-bold focus:outline-none focus:ring-4 focus:ring-kadolar-200 focus:ring-inset"
                      />

                      <div
                        className={`pointer-events-none absolute inset-y-0 right-3 left-3 flex items-center justify-center transition-opacity duration-200 ${searchQuery ? 'opacity-0' : 'opacity-100'}`}
                      >
                        <span className="whitespace-nowrap">
                        <span className="text-gray-500 font-semibold text-[10px]">جستوجو در </span>
                          <span className="text-red-600 font-black text-[14px]">{systemSettings?.site_name || 'کادولار'}</span>
                        </span>
                      </div>
                    </div>

                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Enhanced Logo replaced with uploaded image + dynamic site name */}
          <Link to="/" className="hidden md:flex items-center group lg:justify-self-center lg:justify-center lg:gap-4">
            <div className="relative">
              <div className="w-[104px] h-[104px] flex items-center justify-center transition-all duration-500 transform group-hover:scale-110 overflow-hidden">
                {systemSettings?.logo_url ? (
                  <LogoImage
                    src={resolveMediaUrl(systemSettings.logo_url)}
                    alt={systemSettings?.site_name || 'لوگو'}
                    width="91"
                    height="91"
                    className="max-h-[91px] max-w-[91px] object-contain"
                  />
                ) : (
                  <GiftIcon className="h-16 w-16 text-kadolar-700" />
                )}
              </div>
            </div>
            <div className="mr-5 lg:mr-0 lg:text-center">
              <h1 className="text-4xl font-black bg-gradient-to-r from-kadolar-600 via-kadolar-700 to-kadolar-800 bg-clip-text text-transparent group-hover:from-kadolar-500 group-hover:to-kadolar-700 transition-all duration-300">
                {systemSettings?.site_name || 'کادولار'}
              </h1>
              <p className="text-sm text-gray-600 font-bold tracking-wide lg:text-center group-hover:text-kadolar-600 transition-colors">
                {systemSettings?.header_subtitle || 'فروشگاه هدایای لوکس'}
              </p>
            </div>
          </Link>

          {/* Enhanced Right Side Icons */}
          <div className={`hidden md:flex flex-nowrap items-center justify-end min-w-0 ${showCompactHeader ? 'gap-2 sm:gap-3' : 'gap-3 sm:gap-5'} lg:justify-self-end`}>
            {/* Enhanced Cart */}
            <Link
              to="/cart"
               className="relative h-12 w-12 p-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-colors duration-300 group border-2 border-gray-200 hover:border-kadolar-300 shadow-md hover:shadow-lg"
            >
              <ShoppingCartIcon className="h-7 w-7 text-gray-700 group-hover:text-kadolar-600 transition-colors duration-300" />
              {cartItemsCount > 0 && (
                <span className="hidden md:flex absolute -top-3 -right-3 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-bold rounded-full h-7 w-7 items-center justify-center shadow-xl animate-bounce">
                  {cartItemsCount}
                </span>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-kadolar-400 to-kadolar-600 rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </Link>

            {/* Enhanced User Menu (Profile) */}
            {isAuthenticated ? (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center space-x-3 space-x-reverse p-4 bg-gradient-to-br from-gray-50 to-gray-100 hover:from-kadolar-50 hover:to-kadolar-100 rounded-3xl transition-all duration-500 group border-2 border-gray-200 hover:border-kadolar-300 shadow-lg hover:shadow-2xl transform hover:scale-105">
                  <div className="relative">
                    <UserIcon className="h-7 w-7 text-gray-700 group-hover:text-kadolar-600 transition-colors duration-300" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-green-400 to-green-500 rounded-full border-2 border-white shadow-lg"></div>
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-bold text-gray-800 group-hover:text-kadolar-700 transition-colors duration-300">
                      {userShortName}
                    </p>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-kadolar-400 to-kadolar-600 rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </Menu.Button>

                <Transition
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-150"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-4 w-80 bg-white rounded-3xl shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-[600] border-2 border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-kadolar-500 via-kadolar-600 to-kadolar-700 p-6 text-white">
                      <div className="flex items-center space-x-4 space-x-reverse">
                        <div className="relative">
                          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                            <UserIcon className="h-8 w-8 text-white" />
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{userFullName || 'کاربر'}</h3>
                          <div className="flex items-center mt-2">
                            <SparklesIcon className="h-4 w-4 text-yellow-300 ml-1" />
                            <span className="text-xs text-kadolar-100">عضو طلایی</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="py-2">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={`${
                              active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'
                            } group flex items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                          >
                            <UserIcon className="ml-3 h-5 w-5 text-gray-400 group-hover:text-kadolar-500" />
                            پروفایل من
                          </Link>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/chats"
                            className={`${
                              active ? 'bg-gradient-to-r from-kadolar-50 to-kadolar-100 text-kadolar-700' : 'text-gray-700'
                            } group flex items-center justify-between px-6 py-4 text-sm font-medium transition-all duration-300`}
                          >
                            <span className="flex items-center">
                              <ChatBubbleLeftRightIcon className="ml-3 h-5 w-5 text-gray-400 group-hover:text-kadolar-500" />
                              پیامهای من
                            </span>
                            {chatUnread > 0 && (
                              <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center shadow">
                                {chatUnread > 99 ? '99+' : chatUnread}
                              </span>
                            )}
                          </Link>
                        )}
                      </Menu.Item>
                      
                      <div className="border-t border-gray-200 my-2"></div>
                      
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${
                              active ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700' : 'text-gray-700'
                            } group flex w-full items-center px-6 py-4 text-sm font-medium transition-all duration-300`}
                          >
                            خروج از حساب
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <div className="flex items-center space-x-3 space-x-reverse">
                <Link
                  to="/login"
                  className="px-6 py-3 text-gray-700 hover:text-kadolar-600 font-medium transition-all duration-300 hover:bg-gray-50 rounded-2xl border-2 border-transparent hover:border-gray-200"
                >
                  ورود
                </Link>
                <Link
                  to="/register"
                  className="px-8 py-4 bg-gradient-to-r from-kadolar-500 via-kadolar-600 to-kadolar-700 text-white font-bold rounded-3xl hover:from-kadolar-600 hover:to-kadolar-800 transition-all duration-500 shadow-xl hover:shadow-2xl transform hover:scale-105 border-2 border-kadolar-400 hover:border-kadolar-500"
                >
                  <span className="flex items-center space-x-2 space-x-reverse">
                    <SparklesIcon className="h-5 w-5" />
                    <span>عضویت</span>
                  </span>
                </Link>
              </div>
            )}

            {/* Enhanced Wishlist */}
            <Link
              to="/wishlist"
               className="relative h-12 w-12 p-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 hover:from-red-50 hover:to-pink-50 rounded-2xl transition-colors duration-300 group border-2 border-gray-200 hover:border-red-300 shadow-md hover:shadow-lg"
            >
              <HeartIcon className="h-7 w-7 text-gray-700 group-hover:text-red-500 transition-colors duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </Link>
          </div>
        </div>

        {/* Enhanced Mobile Search (disabled on /chats) */}
        {!isChatsPage && (
        <div className="hidden md:block lg:hidden pb-6">
          <form onSubmit={handleSearch} className="relative group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-kadolar-200 to-kadolar-300 rounded-3xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="جستجو در کادولار..."
                className="w-full pl-16 pr-6 py-4 border-2 border-gray-200 rounded-3xl focus:ring-4 focus:ring-kadolar-200 focus:border-kadolar-400 bg-gradient-to-r from-gray-50 to-white hover:from-white hover:to-gray-50 transition-all duration-500 text-gray-800 placeholder-gray-500 font-medium shadow-lg hover:shadow-xl relative z-10"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <button
                  type="submit"
                  className="p-3 bg-gradient-to-r from-kadolar-500 via-kadolar-600 to-kadolar-700 text-white rounded-2xl hover:from-kadolar-600 hover:to-kadolar-800 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-110"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <SparklesIcon className="h-4 w-4" />
              </div>
            </div>
          </form>
        </div>
        )}
      </div>
      </div>

      {showLoopBarHomeMobile && mainHeaderVisible ? (
        <div
          className="topbar-scrollbar-hide w-full overflow-hidden flex items-center"
          style={{
            height: `${loopBarHeightPx}px`,
            backgroundImage: loopBarBgCss,
            color: loopBarTextColor,
            fontFamily: siteFontFamily,
          }}
        >
          <div
            className="w-full overflow-hidden h-full"
            style={{
              maxWidth: loopBarMaxWidthPx ? `${loopBarMaxWidthPx}px` : undefined,
              margin: loopBarMaxWidthPx ? '0 auto' : undefined,
            }}
          >
            <div className="kadolar-marquee-wrap">
              {loopBarMarquee}
            </div>
          </div>
        </div>
      ) : null}

      {/* Enhanced Mobile Menu */}
      {!isChatsPage && mobileMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-[640]"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className="lg:hidden absolute top-full left-1/2 -translate-x-1/2 w-[70vw] mt-4 bg-white border-2 border-gray-200 rounded-3xl shadow-2xl z-[650] overflow-hidden max-h-[75vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="p-4 space-y-3">
            {/* Enhanced Mobile Navigation */}
            <div className="space-y-[9px]">
              <Link
                to="/categories"
                className="flex items-center px-4 py-3 text-gray-700 hover:text-kadolar-600 hover:bg-gradient-to-r hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-all duration-300 group"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">دسته‌بندی‌ها</span>
              </Link>

              <Link
                to="/orders"
                className="flex items-center px-4 py-3 text-gray-700 hover:text-kadolar-600 hover:bg-gradient-to-r hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-all duration-300 group"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">سفارشات من</span>
              </Link>
              
              <Link
                to="/about"
                className="flex items-center px-4 py-3 text-gray-700 hover:text-kadolar-600 hover:bg-gradient-to-r hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-all duration-300 group"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">درباره ما</span>
              </Link>
              
              <Link
                to="/contact"
                className="flex items-center px-4 py-3 text-gray-700 hover:text-kadolar-600 hover:bg-gradient-to-r hover:from-kadolar-50 hover:to-kadolar-100 rounded-2xl transition-all duration-300 group"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="font-medium">ارتباط با ما</span>
              </Link>

              {isAuthenticated && (
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-3 text-gray-700 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 rounded-2xl transition-all duration-300 group"
                >
                  <span className="font-medium">خروج از حساب</span>
                </button>
              )}
            </div>
            
            {((!isAuthenticated) || canAccessStaff) && (
              <div className="border-t-2 border-gray-200 pt-4 mt-4">
                {!isAuthenticated && (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="block w-full px-6 py-4 text-center text-gray-700 hover:text-kadolar-600 font-medium border-2 border-gray-200 hover:border-kadolar-300 rounded-2xl transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      ورود به حساب
                    </Link>
                    <Link
                      to="/register"
                      className="block w-full px-6 py-4 text-center bg-gradient-to-r from-kadolar-500 via-kadolar-600 to-kadolar-700 text-white font-bold rounded-2xl transition-all duration-500 shadow-xl hover:shadow-2xl transform hover:scale-105"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center justify-center space-x-2 space-x-reverse">
                        <SparklesIcon className="h-5 w-5" />
                        <span>عضویت در کادولار</span>
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </>
      )}
      </header>
    </>
  );
};

export default memo(Header);
